import React, { useState, useEffect } from "react";
import { useTable, usePagination } from "react-table";
import { onValue, ref } from "firebase/database";
import { db } from "../../firebase";
import { sections, subjectsShort } from "../../constants";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import axios from "axios";
import toast from "react-hot-toast";
import "react-datepicker/dist/react-datepicker.css";
import { CSVLink } from "react-csv";
import {
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

moment.tz.setDefault("Asia/Manila");

const columns = [
  {
    Header: "Name",
    accessor: "name",
  },
  {
    Header: "Student Number",
    accessor: "studentNumber",
  },
  {
    Header: "Subject",
    accessor: "subject",
  },
  {
    Header: "Date",
    accessor: "date",
  },
  {
    Header: "Time In",
    accessor: (row) => {
      if (row?.time) {
        return row.time;
      } else {
        return "N/A";
      }
    },
  },
  {
    Header: "Time Out",
    accessor: (row) => {
      if (row?.out) {
        return moment(row["out"]["timestamp"]).format("h:mm A");
      } else {
        return "N/A";
      }
    },
  },
  {
    Header: "Proof of Attendance TI",
    accessor: (row) => {
      if (row?.imageId) {
        return row["imageId"];
      } else {
        return "N/A";
      }
    },
    Cell: ({ value }) => {
      if (value === "N/A") {
        return <div>N/A</div>;
      } else {
        return (
          <a
            href={`https://drive.google.com/file/d/${value}/view`}
            target="_blank"
          >
            <img
              src={`https://drive.google.com/thumbnail?id=${value}`}
              alt="Loading on server side. Please wait to load!"
              className="w-14 h-14 object-scale-down"
            />
          </a>
        );
      }
    },
    // Cell: ({ value }) => (
    //   <a className="text-blue-600" href={value}>
    //     Image Link
    //   </a>
    // ),
  },
  {
    Header: "Proof of Attendance TO",
    accessor: (row) => {
      if (row?.out) {
        return row["out"]["imageId"];
      } else {
        return "N/A";
      }
    },
    Cell: ({ value }) => {
      if (value === "N/A") {
        return <div>N/A</div>;
      } else {
        return (
          <a
            href={`https://drive.google.com/file/d/${value}/view`}
            target="_blank"
          >
            <img
              src={`https://drive.google.com/thumbnail?id=${value}`}
              alt="Loading on server side. Please wait to load!"
              className="w-14 h-14 object-scale-down"
            />
          </a>
        );
      }
    },
    // Cell: ({ value }) => (
    //   <a className="text-blue-600" href={value}>
    //     Image Link
    //   </a>
    // ),
  },
];

const Admin = ({ expanded }) => {
  const [data, setData] = useState([]);
  const [attendances, setAttendances] = useState({});
  const [subjectFilter, setSubjectFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [studentNumberFilter, setStudentNumberFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("present");
  const [absentData, setAbsentData] = useState([]);

  useEffect(() => {
    if (statusFilter == "present") {
      const newData = Object.entries(attendances)
        .flatMap(([key, value]) =>
          Object.values(value).map((x) => {
            return {
              studentNumber: x.studentNumber,
              name: x.studentName,
              subject: subjectsShort[key],
              section: x.section,
              out: x.out,
              date: moment(x.timestamp).format("MMMM DD, YYYY"),
              time: moment(x.timestamp).format("h:mm A"),
              image: `https://drive.google.com/file/d/${x.imageId}/view?usp=drive_link`,
              imageId: x.imageId,
            };
          })
        )
        .filter((item) => {
          const isSubjectMatch =
            item.subject.toLowerCase().includes(subjectFilter.toLowerCase()) ||
            subjectFilter === "";
          const isSectionMatch =
            item.section === sectionFilter || sectionFilter === "";
          const isDateMatch =
            startDate === null ||
            moment(item.date, "MMMM DD, YYYY").isSame(startDate, "day");
          const isStudentNumberMatch =
            item.studentNumber
              .toLowerCase()
              .includes(studentNumberFilter.toLowerCase()) ||
            studentNumberFilter === "";
          const isNameMatch =
            item.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
            nameFilter === "";
          const isPresent = item.out !== undefined;
          return (
            isSubjectMatch &&
            isSectionMatch &&
            isDateMatch &&
            isStudentNumberMatch &&
            isNameMatch &&
            isPresent
          );
        });

      newData.sort(
        (a, b) =>
          moment(b.date, "MMMM DD, YYYY").valueOf() -
          moment(a.date, "MMMM DD, YYYY").valueOf()
      );

      setData(newData);
    }
  }, [
    attendances,
    subjectFilter,
    startDate,
    studentNumberFilter,
    nameFilter,
    sectionFilter,
    statusFilter,
  ]);

  useEffect(() => {
    if (subjectFilter === "" || sectionFilter === "" || startDate === null) {
      setStatusFilter("present");
    }
  }, [subjectFilter, sectionFilter, startDate]);

  useEffect(() => {
    if (statusFilter == "absent") {
      const mappedStudentNumber = data.map((x) => x.studentNumber);
      let filteredAbsent = absentData.filter(
        (x) => x[3] == sectionFilter && !mappedStudentNumber.includes(x[0])
      );

      filteredAbsent = filteredAbsent.map((x) => {
        return {
          studentNumber: x[0],
          name: x[2],
          subject: subjectFilter,
          section: sectionFilter,
          out: false,
          date: moment(startDate).format("MMMM DD, YYYY"),
          time: false,
          image: false,
          imageId: false,
        };
      });

      setData(filteredAbsent);

      console.log(filteredAbsent);
    }
  }, [statusFilter]);

  useEffect(() => {
    const query = ref(db, `subjects`);

    const unsubscribeValue = onValue(query, (snapshot) => {
      const data = snapshot.val();
      if (snapshot.exists()) {
        setAttendances(data);
      }
    });

    const fetchAbsentData = async () => {
      try {
        const response = await axios.get(
          "https://class-trackr-server.onrender.com/students"
        );

        if (response.status === 200) {
          setAbsentData(response.data.data);
        } else {
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Cannot fetch students data");
      }
    };

    fetchAbsentData();

    return () => {
      unsubscribeValue();
    };
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    pageOptions,
    pageCount,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    usePagination
  );

  const exportData = () => {
    const exportedData = data.map((item) => {
      return [
        item.name,
        item.studentNumber,
        item.subject,
        item.date,
        item?.time ? moment(item.time).format("h:mm A") : "N/A",
        item?.out ? moment(item.out.timestamp).format("h:mm A") : "N/A",
        item?.image ? item.image : "N/A",
        item?.out
          ? `https://drive.google.com/file/d/${item.out.imageId}/view?usp=drive_link`
          : "N/A",
      ];
    });

    return [
      [
        "Name",
        "Student Number",
        "Subject",
        "Date",
        "Time In",
        "Time Out",
        "Proof of Attendance TI",
        "Proof of Attendance TO",
      ],
      ...exportedData,
    ];
  };

  const generateFilename = () => {
    let filename = "attendance";
    if (subjectFilter) {
      filename += `_${subjectFilter}`;
    }
    if (sectionFilter) {
      filename += `_${sectionFilter}`;
    }
    if (startDate) {
      filename += `_${moment(startDate).format("YYYY-MM-DD")}`;
    }
    if (studentNumberFilter) {
      filename += `_${studentNumberFilter}`;
    }
    if (nameFilter) {
      filename += `_${nameFilter}`;
    }
    return filename + ".csv";
  };

  return (
    <div
      className={`ml-0 mt-16 ${
        expanded ? "md:ml-[15.5rem]" : "md:ml-20"
      } px-10 mt-20 mb-10`}
    >
      <div className="flex flex-col md:flex-row md:items-center mb-4">
        <CSVLink
          data={exportData()}
          filename={generateFilename()}
          className="bg-[#198754] hover:bg-green-600 text-white text-sm font-bold py-2 px-4 rounded text-center"
        >
          Export to CSV
        </CSVLink>
      </div>
      <div className="flex flex-col md:flex-row gap-4 md:gap-12 mb-4">
        <div className="flex flex-col md:flex-row md:items-center">
          <label htmlFor="subjectFilter" className="mr-2">
            Subject:
          </label>
          <select
            id="subjectFilter"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(subjectsShort).map(([key, value]) => (
              <option key={key} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col md:flex-row md:items-center">
          <label htmlFor="sectionFilter" className="mr-2">
            Section:
          </label>
          <select
            id="sectionFilter"
            value={sectionFilter}
            className="md:px-3"
            onChange={(e) => setSectionFilter(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(sections).map(([key, value]) => (
              <option key={key} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col md:flex-row md:items-center">
          <label htmlFor="dateFilter" className="mr-2">
            Date:
          </label>
          <div>
            <DatePicker
              id="dateFilter"
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="MMMM dd, yyyy"
              isClearable
              className="border-gray-500 border-[1px] rounded-sm"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center">
          <label htmlFor="statusFilter" className="mr-2">
            Status:
          </label>
          <select
            id="statusFilter"
            value={statusFilter}
            className="md:px-2"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="present">Present</option>
            {!(subjectFilter === "" || subjectFilter === null) &&
            !(sectionFilter === "" || sectionFilter === null) &&
            !(startDate === "" || startDate === null) ? (
              <option value="absent">Absent</option>
            ) : (
              <option
                disabled
                value="absent"
                style={{
                  backgroundColor: "gray",
                  cursor: "not-allowed",
                }}
              >
                Absent
              </option>
            )}
          </select>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-4 md:gap-12 mb-4">
        <div className="flex flex-col md:flex-row md:items-center">
          <label htmlFor="studentNumberFilter" className="mr-2">
            Student Number:
          </label>
          <input
            className="border-gray-500 border-[1px] rounded-sm px-1"
            type="text"
            id="studentNumberFilter"
            value={studentNumberFilter}
            onChange={(e) => setStudentNumberFilter(e.target.value)}
          />
        </div>
        <div className="flex flex-col md:flex-row md:items-center">
          <label htmlFor="nameFilter" className="mr-2">
            Name:
          </label>
          <input
            className="border-gray-500 border-[1px] rounded-sm px-1"
            type="text"
            id="nameFilter"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table
          className="table-auto border-collapse w-full"
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className="border px-4 py-2 bg-gray-100 text-left text-gray-700 font-medium uppercase tracking-wider"
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} className="border px-4 py-2">
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="h-4" />
      <div className="flex justify-center gap-2 mt-4">
        <button
          className="mr-2"
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
        >
          <ChevronFirst className="hover:scale-125" />
        </button>{" "}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          <ChevronLeft className="hover:scale-125" />
        </button>{" "}
        <span>
          Page {pageIndex + 1} of {pageOptions.length}
        </span>{" "}
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          <ChevronRight className="hover:scale-125" />
        </button>{" "}
        <button
          className="ml-2"
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
        >
          <ChevronLast className="hover:scale-125" />
        </button>{" "}
      </div>
    </div>
  );
};

export default Admin;
